"use client";

import type { Alignment } from "@/app/_api/generateTts";
import type { Voice } from "@/app/_data/voices";
import type { IUseCasePromptWithId } from "@components/languages/types";
import type { Media } from "@mw/shared/payload-types";
import type { PlayingState } from "@ui/hooks/useTtsGeneration";

import { PromptFeature, getVoiceToUseCasePromptAssignment } from "@components/languages/types";
import { SliderGeneric } from "@ui/components/composers/SliderGeneric";
import { Avatar } from "@ui/components/core/Avatar";
import { Button } from "@ui/components/core/Button";
import { Image } from "@ui/components/core/Image";
import { getVoiceImage } from "@ui/components/helpers/voiceUtils";
import { useTtsGeneration } from "@ui/hooks/useTtsGeneration";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import { cn } from "@ui/lib/utils";
import { Pause, Pencil, Play } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { MultiSpeakerInputRef, MultiSpeakerSample } from "../MultiSpeakerInput";
import type { DemoPrompts } from "../config";

import { LimitReachedOverlay } from "../../LimitReachedOverlay";
import { MultiSpeakerInput } from "../MultiSpeakerInput";
import { TextHighlighter } from "../TextHighlighter";
import { convertDialogueToSamples } from "../ttsUtilts";
import { TextAreaWithHighlighter } from "./TTSTextInput";

const BACKGROUND_IMAGES: Media[] = [
  {
    url: "/assets/images/v3/bg-1.jpg",
    id: "j1wfeoyc1pe",
    updatedAt: "2025-05-29T15:21:53.000Z",
    createdAt: "2025-05-29T15:21:53.000Z",
    mimeType: "image/jpeg",
    width: 1000,
    height: 1000,
  },
  {
    url: "/assets/images/v3/bg-3.jpg",
    id: "j1wfeoyc1pe",
    updatedAt: "2025-05-29T15:21:53.000Z",
    createdAt: "2025-05-29T15:21:53.000Z",
    mimeType: "image/jpeg",
    width: 1000,
    height: 1000,
  },
  {
    url: "/assets/images/v3/bg-2.jpg",
    id: "j1wfeoyc1pe",
    updatedAt: "2025-05-29T15:21:53.000Z",
    createdAt: "2025-05-29T15:21:53.000Z",
    mimeType: "image/jpeg",
    width: 1000,
    height: 1000,
  },
  {
    url: "/assets/images/v3/bg-4.jpg",
    id: "j1wfeoyc1pe",
    updatedAt: "2025-05-29T15:21:53.000Z",
    createdAt: "2025-05-29T15:21:53.000Z",
    mimeType: "image/jpeg",
    width: 1000,
    height: 1000,
  },
  {
    url: "/assets/images/v3/bg-5.jpg",
    id: "j1wfeoyc1pe",
    updatedAt: "2025-05-29T15:21:53.000Z",
    createdAt: "2025-05-29T15:21:53.000Z",
    mimeType: "image/jpeg",
    width: 1000,
    height: 1000,
  },
];

interface EditorCardProps {
  assignedVoices: Voice[];
  backgroundImage: Media;
  className?: string;
  onLimitReached: () => void;
  prompt: IUseCasePromptWithId;
}

interface EditorCardsProps {
  className?: string;
  demoPrompts?: DemoPrompts;
  voices: Voice[];
}

const CardHeader = ({ assignedVoices, description }: { assignedVoices: Voice[]; description: string }) => {
  return (
    <div className='mb-24 flex items-center gap-12'>
      <div className='relative h-40 w-40'>
        {assignedVoices.map((voice, voiceIndex) => (
          <Avatar
            className={cn(
              "effect-color absolute h-40 w-40 border-2 border-inverse group-hover:border-default",
              voiceIndex > 0 && "-ml-6 lg:-ml-12",
            )}
            height={20}
            image={getVoiceImage(voice, "md")}
            key={voice.voiceId}
            size='md'
            width={20}
            zIndex={assignedVoices.length - voiceIndex}
          />
        ))}
      </div>
      <div className='flex flex-col'>
        <span className='f-ui-03 text-inverse'>{assignedVoices.map(voice => voice.name).join(" & ")}</span>
        <span className='f-paragraph-04 text-inverse-light'>{description}</span>
      </div>
    </div>
  );
};

const Editor = ({
  prompt,
  assignedVoices,
  initialDialogue,
  markNeedsRegeneration,
  multiSpeakerRef,
  alignment,
  audioRef,
  handleTextChange,
  playingState,
  text,
}: {
  alignment: Alignment | null;
  assignedVoices: Voice[];
  audioRef: React.RefObject<HTMLAudioElement | null>;
  handleTextChange: (text: string) => void;
  initialDialogue: MultiSpeakerSample[];
  markNeedsRegeneration: () => void;
  multiSpeakerRef: React.RefObject<MultiSpeakerInputRef | null>;
  playingState: PlayingState;
  prompt: IUseCasePromptWithId;
  text: string;
}) => {
  return (
    <div className='h-full'>
      {prompt.dialogue ? (
        <MultiSpeakerInput
          availableVoices={assignedVoices}
          className='h-full text-inverse'
          initialSamples={initialDialogue}
          onChange={markNeedsRegeneration}
          onDirty={markNeedsRegeneration}
          ref={multiSpeakerRef}
          variant='simple'
        />
      ) : (
        <TextAreaWithHighlighter
          alignment={alignment}
          audioElementRef={audioRef}
          className='h-full p-0 text-inverse lg:px-0'
          isStreaming
          onTextChange={handleTextChange}
          playingState={playingState}
          text={text}
          withBrackets={false}
        />
      )}
    </div>
  );
};

const StaticRenderer = ({
  prompt,
  showHighlighter,
  alignment,
  displayCharacters,
  audioRef,
  text,
}: {
  alignment: Alignment | null;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  displayCharacters: string[];
  prompt: IUseCasePromptWithId;
  showHighlighter: boolean;
  text: string;
}) => {
  return (
    <div className='whitespace-pre-wrap text-inverse'>
      {prompt.dialogue ? (
        // Display dialogue with speaker names and text
        <div className='space-y-8'>
          {prompt.dialogue?.map((item, index) => (
            <div className='space-y-8' key={index}>
              <div className='f-ui-03 text-inverse-light opacity-70'>{item.speaker}</div>
              <div className='text-inverse'>{item.text}</div>
            </div>
          ))}
        </div>
      ) : showHighlighter ? (
        <TextHighlighter
          characters={alignment?.characters ?? []}
          className={cn("scrollbar-none h-full flex-1 overflow-y-auto text-inverse")}
          displayCharacters={displayCharacters}
          endTimes={alignment?.character_end_times_seconds ?? []}
          mediaElement={audioRef.current}
          startTimes={alignment?.character_start_times_seconds ?? []}
          withBrackets={false}
        />
      ) : (
        text
      )}
    </div>
  );
};

const CardFooter = ({
  playingState,
  text,
  prompt,
  regenerate,
  handlePlayPause,
  handleGenerate,
  isEditing,
  handleEdit,
}: {
  handleEdit: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleGenerate: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handlePlayPause: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isEditing: boolean;
  playingState: PlayingState;
  prompt: IUseCasePromptWithId;
  regenerate: boolean;
  text: string;
}) => {
  const t = useTranslations("ClientKeys.TTS");
  const tG = useTranslations("ClientKeys.Globals");
  return (
    <div className='mt-24 flex items-center justify-between'>
      <Button
        disabled={playingState === "loading" || (!text && !prompt.dialogue)}
        onClick={!regenerate ? handlePlayPause : handleGenerate}
        size='md'
        variant='elevated'
      >
        {playingState === "loading" ? (
          <>
            <Spinner20 className='animate-spin' />
          </>
        ) : (
          <>
            {playingState === "playing" ? (
              <Pause className='mr-8 h-16 w-16' fill='currentColor' />
            ) : (
              <Play className='mr-8 h-16 w-16' fill='currentColor' />
            )}
            {playingState === "playing" ? t("widget.pause") : tG("play")}
          </>
        )}
      </Button>

      {!isEditing && (
        <Button
          className='effect-opacity bg-overlay-light text-inverse opacity-0 backdrop-blur-sm group-hover:opacity-100'
          onClick={handleEdit}
          size='pill'
          variant='elevated'
        >
          <Pencil className='h-16 w-16' fill='currentColor' />
        </Button>
      )}
    </div>
  );
};

const EditorCard = ({ prompt, assignedVoices, className, backgroundImage, onLimitReached }: EditorCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showHighlighter, setShowHighlighter] = useState(false);
  const multiSpeakerRef = useRef<MultiSpeakerInputRef>(null);

  const [text, setText] = useState(prompt.text);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Use the TTS generation hook
  const { generate, playingState, setPlayingState, alignment, regenerate, setRegenerate, showLimitReachedOverlay } =
    useTtsGeneration(
      text,
      assignedVoices[0],
      1, // speed
      true, // useStreaming
      "editor_cards",
    );

  const displayCharacters = useMemo(() => {
    return text.split("");
  }, [text]);

  const initialDialogue = useMemo(() => {
    return convertDialogueToSamples(prompt.dialogue || [], assignedVoices);
  }, [prompt.dialogue, assignedVoices]);

  useEffect(() => {
    if (showLimitReachedOverlay) {
      onLimitReached();
    }
  }, [showLimitReachedOverlay]);

  const handleGenerate = useCallback(async () => {
    if (!audioRef.current) return;
    setShowHighlighter(true);

    if (prompt.dialogue) {
      const samples = convertDialogueToSamples(
        multiSpeakerRef.current?.getSamples().map(sample => ({
          speaker: sample.speakerName,
          text: sample.text,
          voice_id: sample.voiceId,
        })) || prompt.dialogue,
        assignedVoices,
      );

      await generate({
        isDialogueMode: true,
        dialogueModeSamples: samples,
        modelId: "eleven_v3",
        audioElement: audioRef.current,
      });
    } else {
      await generate({
        textPrompt: text,
        audioElement: audioRef.current,
        modelId: "eleven_v3",
      });
    }
  }, [text, prompt.dialogue, assignedVoices, generate]);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (playingState === "playing" || playingState === "loading") {
      audioRef.current.pause();
      setPlayingState("paused");
    } else {
      audioRef.current.play();
      setPlayingState("playing");
    }
  }, [playingState]);

  const handleEdit = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleTextChange = useCallback((newText: string) => {
    setText(newText);
    markNeedsRegeneration();
  }, []);

  const markNeedsRegeneration = useCallback(() => {
    setRegenerate(true);
  }, []);

  const handleAudioEnded = useCallback(() => {
    setPlayingState("idle");
  }, []);

  const handleAudioPlay = useCallback(() => {
    setPlayingState("playing");
  }, []);

  const handleAudioPause = useCallback(() => {
    setPlayingState("paused");
  }, []);

  const handleCardClick = useCallback(() => {
    if (!isEditing && playingState === "idle") {
      handleGenerate();
    } else if (!isEditing) {
      handlePlayPause();
    }
  }, [isEditing, handleGenerate, playingState, handlePlayPause]);

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleCardClick();
      }
    },
    [handleCardClick],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={cn(
        "slider-element group relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-xl p-16 pb-24 lg:p-24",
        !isEditing && "cursor-pointer",
        className,
      )}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      ref={cardRef}
      tabIndex={isEditing ? -1 : 0}
    >
      <Image alt='Background' className='absolute inset-0 object-cover' resource={backgroundImage} />
      {/* Content container */}
      <div className='relative z-10 flex h-full w-full max-w-4xl flex-col'>
        <CardHeader assignedVoices={assignedVoices} description={prompt.description} />

        {/* Transcript content */}
        <div className='scrollbar-none f-paragraph-02 flex-1 overflow-y-auto'>
          {isEditing ? (
            <Editor
              alignment={alignment}
              assignedVoices={assignedVoices}
              audioRef={audioRef}
              handleTextChange={handleTextChange}
              initialDialogue={initialDialogue}
              markNeedsRegeneration={markNeedsRegeneration}
              multiSpeakerRef={multiSpeakerRef}
              playingState={playingState}
              prompt={prompt}
              text={text}
            />
          ) : (
            <StaticRenderer
              alignment={alignment}
              audioRef={audioRef}
              displayCharacters={displayCharacters}
              prompt={prompt}
              showHighlighter={showHighlighter}
              text={text}
            />
          )}
        </div>

        {/* Action buttons */}
        <CardFooter
          handleEdit={handleEdit}
          handleGenerate={handleGenerate}
          handlePlayPause={handlePlayPause}
          isEditing={isEditing}
          playingState={playingState}
          prompt={prompt}
          regenerate={regenerate}
          text={text}
        />
      </div>

      {/* Hidden audio element */}
      <audio
        className='hidden'
        onEnded={handleAudioEnded}
        onPause={handleAudioPause}
        onPlay={handleAudioPlay}
        ref={audioRef}
      />
    </div>
  );
};

export const EditorCards = ({ demoPrompts, voices, className }: EditorCardsProps) => {
  const [showLimitReached, setShowLimitReached] = useState(false);
  const promptItems = useMemo(() => {
    if (!demoPrompts) return [];

    return getVoiceToUseCasePromptAssignment(voices, demoPrompts, {
      maxPrompts: 6,
      distributeFeatures: [PromptFeature.AUDIO_TAGS, PromptFeature.DIALOGUE],
      includeUntagged: false,
    }).map(({ assignedVoices, prompt }) => {
      return {
        prompt,
        assignedVoices,
        id: prompt.id,
      };
    });
  }, [demoPrompts, voices]);

  const sliderItems = useMemo(() => {
    return promptItems.map((item, index) => (
      <div className='h-[375px]' key={item.id}>
        <EditorCard
          assignedVoices={item.assignedVoices}
          backgroundImage={BACKGROUND_IMAGES[index % BACKGROUND_IMAGES.length]}
          key={item.id}
          onLimitReached={() => setShowLimitReached(true)}
          prompt={item.prompt}
        />
      </div>
    ));
  }, [promptItems]);

  if (promptItems.length === 0) {
    return null;
  }

  return (
    <div className={cn("h-full", className)}>
      {showLimitReached ? (
        <div className='relative h-[375px] overflow-hidden rounded-xl border border-default'>
          <LimitReachedOverlay />
        </div>
      ) : (
        <SliderGeneric floatingControls imageSelector='.slider-element' items={sliderItems} />
      )}
    </div>
  );
};

export default EditorCards;
